import { TPatientOption } from "../types";

export const normalizePatientPhoneForCall = (
  phone: string | null | undefined,
): string => {
  const normalizedPhone = (phone || "").trim();
  if (!normalizedPhone || normalizedPhone === "+1") {
    return "";
  }
  return normalizedPhone;
};

export const getPhoneDigits = (value: string | null | undefined): string =>
  (value || "").replace(/\D/g, "");

export const normalizeDialerCountryCode = (value: string): string => {
  const digits = getPhoneDigits(value);
  return digits ? `+${digits}` : "+";
};

export const normalizeDialerLocalNumber = (value: string): string =>
  getPhoneDigits(value);

export const buildDialerE164Number = (
  countryCode: string,
  localNumber: string,
): string => {
  const countryCodeDigits = getPhoneDigits(countryCode);
  const localDigits = getPhoneDigits(localNumber);
  if (!countryCodeDigits || !localDigits) {
    return "";
  }
  return `+${countryCodeDigits}${localDigits}`;
};

export const splitDialerE164Number = (
  value: string,
  fallbackCountryCode: string,
): {
  countryCode: string;
  localNumber: string;
} => {
  const normalizedValue = (value || "").trim();
  const digits = getPhoneDigits(normalizedValue);
  const normalizedFallbackCountryCode =
    normalizeDialerCountryCode(fallbackCountryCode);

  if (!digits) {
    return {
      countryCode: normalizedFallbackCountryCode,
      localNumber: "",
    };
  }

  if (!normalizedValue.startsWith("+")) {
    return {
      countryCode: normalizedFallbackCountryCode,
      localNumber: digits,
    };
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return {
      countryCode: "+1",
      localNumber: digits.slice(1),
    };
  }

  const prefixLength = Math.min(3, Math.max(1, digits.length - 10));
  return {
    countryCode: `+${digits.slice(0, prefixLength)}`,
    localNumber: digits.slice(prefixLength),
  };
};

export const dialerE164LooksValid = (value: string): boolean => {
  return /^\+[1-9]\d{7,14}$/.test(value.trim());
};

export const getPhoneMatchTokens = (
  value: string | null | undefined,
): Set<string> => {
  const digits = getPhoneDigits(value);
  const tokens = new Set<string>();

  if (!digits) {
    return tokens;
  }

  tokens.add(digits);

  if (digits.length === 10) {
    tokens.add(`1${digits}`);
  } else if (digits.length === 11 && digits.startsWith("1")) {
    tokens.add(digits.slice(1));
  }

  return tokens;
};

export const phonesLikelyMatch = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => {
  const leftTokens = getPhoneMatchTokens(left);
  const rightTokens = getPhoneMatchTokens(right);

  if (!leftTokens.size || !rightTokens.size) {
    return false;
  }

  for (const token of leftTokens) {
    if (rightTokens.has(token)) {
      return true;
    }
  }

  return false;
};

export const findPatientByPhone = (
  patientOptions: TPatientOption[],
  phone: string,
): TPatientOption | null => {
  return (
    patientOptions.find((patient) =>
      phonesLikelyMatch(normalizePatientPhoneForCall(patient.phone), phone),
    ) || null
  );
};
